import Catcher from './Catcher';
import type { CatcherComponent } from './models/CatcherComponent';
import style from './styles/App.module.scss';
import { useSize } from './hooks/useSize';
import { isPrimaryClick, isSmallStyle } from './functions';
import type { MouseEvent, MouseEventHandler, PropsWithChildren } from 'react';
import { useRef, useState } from 'react';

type SliderProps = PropsWithChildren<{
    onClick: (e: MouseEvent, percent?: number) => void,
    onMarkerDown: MouseEventHandler,
    onUp: MouseEventHandler,
    onMove: (e: MouseEvent, percent?: number, force?: boolean) => void,
    markerLeft: string // e.g., '100px' or '100%'
}>

export default function Slider({
    children,
    onClick,
    onMarkerDown,
    onUp,
    onMove,
    markerLeft,
}: SliderProps) {
    const wholeScrubber = useRef<HTMLDivElement>();
    const catcher = useRef<CatcherComponent>();
    const sizeStyle = useSize();

    const [hasBeenGrabbed, setHasBeenGrabbed] = useState<boolean>(false);
    const [dragTimerId, setDragTimerId] = useState<NodeJS.Timeout>(null);

    const percentFromClick = (x: number): number => {
        const scrubberRect = wholeScrubber.current.getBoundingClientRect();
        let innerLeft = x - scrubberRect.left;
        if (innerLeft < 0) innerLeft = 0;
        else if (innerLeft > scrubberRect.width) innerLeft = scrubberRect.width;
        return innerLeft / scrubberRect.width;
    };

    const handleMarkerDown: MouseEventHandler = (e) => {
        catcher.current.enable();
        setHasBeenGrabbed(true);
        onMarkerDown(e);
    };

    const handleUp: MouseEventHandler = (e) => {
        catcher.current.disable();
        setHasBeenGrabbed(false);
        clearTimeout(dragTimerId);
        setDragTimerId(null);
        onUp(e);
    };

    const handleClick: MouseEventHandler = (e) => {
        if (isPrimaryClick(e)) {
            onClick(e, percentFromClick(e.clientX));
            setDragTimerId(setTimeout(handleMarkerDown.bind(null, e), 100));
        }
    };

    const handleMove: MouseEventHandler = (e) => {
        setDragTimerId(null);
        onMove(e, percentFromClick(e.clientX));
    };
    return (
        <div
            className={`${ style.slider } ${ hasBeenGrabbed ? style.grabbed : '' } ${ isSmallStyle(sizeStyle) ? sizeStyle.slider : '' }`}
            ref={wholeScrubber}
            onMouseUp={handleUp}
            onMouseMove={handleMove}
        >
            <div className={style.clickZone} onMouseDown={handleClick} />
            <div className={style.doneZone} style={{ width: markerLeft }} />
            <div
                className={style.marker}
                style={{ left: markerLeft }}
            >
                <div className={style.grabbable} onMouseDown={handleMarkerDown} />
            </div>
            {children}
            <Catcher ref={catcher} release={handleUp} move={handleMove} />
        </div>
    );
}